// Domain: Role Guard
// Conditional component rendering based on roles and permissions

import React from "react";
import { useRole, usePermission, usePermissions, useOpportunityAccess, useKYCAccess } from "./role.middleware";
import { PERMISSIONS } from "./role.constants";

// Guard component for specific permissions
export const PermissionGuard = ({
    children,
    permission,
    fallback = null,
    loadingComponent = <div>Verificando permisos...</div>
}) => {
    const { isInitialized } = useRole();
    const hasPermission = usePermission(permission);

    if (!isInitialized) {
        return loadingComponent;
    }

    if (!hasPermission) {
        return fallback;
    }

    return children;
};

// Guard component for multiple permissions (any)
export const AnyPermissionGuard = ({
    children,
    permissions,
    fallback = null,
    loadingComponent = <div>Verificando permisos...</div>
}) => {
    const { isInitialized } = useRole();
    const { hasAnyPermission } = usePermissions();

    if (!isInitialized) {
        return loadingComponent;
    }

    if (!hasAnyPermission(permissions)) {
        return fallback;
    }

    return children;
};

// Guard component for multiple permissions (all)
export const AllPermissionsGuard = ({ 
    children,
    permissions,
    fallback = null,
    loadingComponent = <div>Verificando permisos...</div>
}) => {
    const { isInitialized } = useRole();
    const { hasAllPermissions } = usePermissions();

    if (!isInitialized) {
        return loadingComponent;
    }

    if (!hasAllPermissions(permissions)) {
        return fallback;
    }

    return children;
};

// Guard for opportunity access
export const OpportunityGuard = ({ 
    children,
    opportunity,
    action = "view", // 'view', 'edit', 'approve'
    fallback = null,
    loadingComponent = <div>Verificando acceso...</div>
}) => {
    const { isInitialized } = useRole();
    const { canView, canEdit, canApprove } = useOpportunityAccess(opportunity);

    if (!isInitialized) {
        return loadingComponent;
    }

    let hasAccess = false;
    switch (action) {
    case "view":
        hasAccess = canView;
        break;
    case "edit":
        hasAccess = canEdit;
        break;
    case "approve":
        hasAccess = canApprove;
        break;
    default:
        hasAccess = canView;
    }

    if (!hasAccess) {
        return fallback || (
            <div style={{ padding: "20px", textAlign: "center" }}>
                <h3>Acceso Denegado</h3>
                <p>No tienes permisos para {action} esta oportunidad.</p>
            </div>
        );
    }

    return children;
};

// Guard for KYC access
export const KYCGuard = ({ 
    children,
    action = "view", // 'view', 'approve'
    fallback = null,
    loadingComponent = <div>Verificando permisos KYC...</div>
}) => {
    const { isInitialized } = useRole();
    const { canView, canApprove } = useKYCAccess();

    if (!isInitialized) {
        return loadingComponent;
    }

    let hasAccess = false;
    switch (action) {
    case "view":
        hasAccess = canView;
        break;
    case "approve":
        hasAccess = canApprove;
        break;
    default:
        hasAccess = canView;
    }

    if (!hasAccess) {
        return fallback || (
            <div style={{ padding: "20px", textAlign: "center" }}>
                <h3>Acceso Denegado</h3>
                <p>No tienes permisos para {action} KYC.</p>
            </div>
        );
    }

    return children;
};

// Guard for task management
export const TaskGuard = ({ 
    children,
    action = "view", // 'view', 'create', 'assign', 'update'
    fallback = null,
    loadingComponent = <div>Verificando permisos de tareas...</div>
}) => {
    const { isInitialized } = useRole();
    const { hasPermission } = usePermissions();

    if (!isInitialized) {
        return loadingComponent;
    }

    let hasAccess = false;
    switch (action) {
    case "view":
        hasAccess = hasPermission(PERMISSIONS.VIEW_TASKS);
        break;
    case "create":
        hasAccess = hasPermission(PERMISSIONS.CREATE_TASKS);
        break;
    case "assign":
        hasAccess = hasPermission(PERMISSIONS.ASSIGN_TASKS);
        break;
    case "update":
        hasAccess = hasPermission(PERMISSIONS.UPDATE_TASKS);
        break;
    default:
        hasAccess = hasPermission(PERMISSIONS.VIEW_TASKS);
    }

    if (!hasAccess) {
        return fallback || (
            <div style={{ padding: "20px", textAlign: "center" }}>
                <h3>Acceso Denegado</h3>
                <p>No tienes permisos para {action} tareas.</p>
            </div>
        );
    }

    return children;
};

// Guard for document management
export const DocumentGuard = ({ 
    children,
    action = "view", // 'view', 'upload', 'validate'
    fallback = null,
    loadingComponent = <div>Verificando permisos de documentos...</div>
}) => {
    const { isInitialized } = useRole();
    const { hasPermission } = usePermissions();

    if (!isInitialized) {
        return loadingComponent;
    }

    let hasAccess = false;
    switch (action) {
    case "view":
        hasAccess = hasPermission(PERMISSIONS.VIEW_DOCUMENTS);
        break;
    case "upload":
        hasAccess = hasPermission(PERMISSIONS.UPLOAD_DOCUMENTS);
        break;
    case "validate":
        hasAccess = hasPermission(PERMISSIONS.VALIDATE_DOCUMENTS);
        break;
    default:
        hasAccess = hasPermission(PERMISSIONS.VIEW_DOCUMENTS);
    }

    if (!hasAccess) {
        return fallback || (
            <div style={{ padding: "20px", textAlign: "center" }}>
                <h3>Acceso Denegado</h3>
                <p>No tienes permisos para {action} documentos.</p>
            </div>
        );
    }

    return children;
};

// Admin only guard
export const AdminGuard = ({ 
    children,
    fallback = null,
    loadingComponent = <div>Verificando permisos de administrador...</div>
}) => {
    const { isInitialized, currentUserRole } = useRole();

    if (!isInitialized) {
        return loadingComponent;
    }

    if (currentUserRole !== "ADMIN") {
        return fallback || (
            <div style={{ padding: "20px", textAlign: "center" }}>
                <h3>Acceso Denegado</h3>
                <p>Solo los administradores pueden acceder a esta funcionalidad.</p>
            </div>
        );
    }

    return children;
};

// Agent only guard
export const AgentGuard = ({ 
    children,
    fallback = null,
    loadingComponent = <div>Verificando permisos de agente...</div>
}) => {
    const { isInitialized, currentUserRole } = useRole();

    if (!isInitialized) {
        return loadingComponent;
    }

    if (currentUserRole !== "AGENT") {
        return fallback || (
            <div style={{ padding: "20px", textAlign: "center" }}>
                <h3>Acceso Denegado</h3>
                <p>Solo los agentes pueden acceder a esta funcionalidad.</p>
            </div>
        );
    }

    return children;
};

// Legal team guard
export const LegalGuard = ({ 
    children,
    fallback = null,
    loadingComponent = <div>Verificando permisos del equipo legal...</div>
}) => {
    const { isInitialized, currentUserRole } = useRole();

    if (!isInitialized) {
        return loadingComponent;
    }

    if (!["LEGAL", "ADMIN"].includes(currentUserRole)) {
        return fallback || (
            <div style={{ padding: "20px", textAlign: "center" }}>
                <h3>Acceso Denegado</h3>
                <p>Solo el equipo legal puede acceder a esta funcionalidad.</p>
            </div>
        );
    }

    return children;
}; 